import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
  Avatar,
  FormControl,
  Select,
  MenuItem,
  Alert,
  LinearProgress
} from '@mui/material';
import api, { inspectionTypesApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ShareIcon from '@mui/icons-material/Share';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TerrainIcon from '@mui/icons-material/Terrain';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DonutSmallIcon from '@mui/icons-material/DonutSmall';
import PercentIcon from '@mui/icons-material/Percent';
import CancelIcon from '@mui/icons-material/Cancel';
import DisciplineAccessControl from '../common/DisciplineAccessControl';
import SlopeReportPreviewModal from './SlopeReportPreviewModal';

// Cycle options based on inspection frequency
const getCycleOptions = (frequency) => {
  if (!frequency) return [];
  const map = {
    weekly: { max: 52, name: 'Week' },
    monthly: { max: 12, name: 'Month' },
    quarterly: { max: 4, name: 'Quarter' },
    biannual: { max: 2, name: 'Half-Year' },
    annual: { max: 1, name: 'Year' },
    'ad-hoc': { max: 1, name: 'Ad-hoc' },
    bimonthly: { max: 6, name: 'Period' },
    every_4_months: { max: 3, name: 'Period' },
    every_18_months: { max: 1, name: 'Period' },
    biennial: { max: 1, name: 'Period' },
  };
  const config = map[frequency.toLowerCase()] || { max: 1, name: 'Cycle' };
  return Array.from({ length: config.max }, (_, i) => ({
    value: i + 1,
    label: `${config.name} ${i + 1}`,
  }));
};

// Status display config
const statusConfig = {
  draft: { label: 'Draft', color: '#ff9800' },
  pending_analyze: { label: 'Pending Analyze', color: '#2196f3' },
  in_progress: { label: 'In Progress', color: '#9c27b0' },
  rated: { label: 'Rated', color: '#4caf50' },
};

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

// Shared dropdown FormControl styling (DefectsPage pattern)
const dropdownSx = (theme) => ({
  minWidth: 200,
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: 'white',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
    border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
    height: '40px',
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.04),
      borderColor: alpha(theme.palette.primary.main, 0.3),
    },
    '&.Mui-focused': {
      bgcolor: 'white',
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  '& .MuiSelect-select': {
    fontWeight: 400,
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    height: '40px',
    paddingTop: '0',
    paddingBottom: '0',
  },
});

const dropdownMenuProps = (theme) => ({
  PaperProps: {
    sx: {
      mt: 1,
      boxShadow: theme.shadows[3],
      borderRadius: '8px',
      '& .MuiMenuItem-root': {
        py: 0.75,
        px: 1.5,
        minHeight: 'auto',
        fontSize: '0.875rem',
        '&:hover': {
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        },
        '&.Mui-selected': {
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.12),
          },
        },
      },
    },
  },
});

const SlopeReportsPage = () => {
  const theme = useTheme();
  const { getUserOrganization } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [stats, setStats] = useState({
    totalReports: 0,
    latestReportDate: null,
    loading: true
  });

  // Coverage Tracker state
  const [inspectionTypes, setInspectionTypes] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [selectedType, setSelectedType] = useState(null);
  const [selectedCycle, setSelectedCycle] = useState('');
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [coverageData, setCoverageData] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);

  // Report generation modal state
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);

  // Batch generation state
  const [batchMode, setBatchMode] = useState(false);
  const [batchQueue, setBatchQueue] = useState([]);
  const [batchIndex, setBatchIndex] = useState(0);
  const [batchResults, setBatchResults] = useState([]);

  // Fetch reports from backend
  const fetchReports = useCallback(async () => {
    setLoadingReports(true);
    try {
      const response = await api.get('/reports', {
        params: {
          report_type: 'inspection_report',
          asset_type: 'Slope',
          limit: 100
        }
      });
      console.log('Fetched slope reports:', response.data);
      setReports(response.data.data || []);

      // Update stats
      const reportData = response.data.data || [];
      setStats({
        totalReports: reportData.length,
        latestReportDate: reportData.length > 0 ? reportData[0].created_at : null,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching slope reports:', error);
      setReports([]);
      setStats({
        totalReports: 0,
        latestReportDate: null,
        loading: false
      });
    } finally {
      setLoadingReports(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Fetch inspection types for Slope asset type on mount
  useEffect(() => {
    const fetchInspectionTypes = async () => {
      try {
        const orgId = getUserOrganization()?.id;
        if (!orgId) return;

        // Get Slope asset_type_id
        const assetTypesRes = await api.get('/asset-types');
        const slopeType = (assetTypesRes.data || []).find(t => t.name === 'Slope');
        if (!slopeType) return;

        const res = await inspectionTypesApi.getTypes({
          organization_id: orgId,
          asset_type_id: slopeType.id,
        });
        setInspectionTypes(res.data || []);
      } catch (error) {
        console.error('Error fetching inspection types:', error);
      }
    };
    fetchInspectionTypes();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch coverage data when all three selections are made
  useEffect(() => {
    if (!selectedTypeId || !selectedCycle || !selectedYear) {
      setCoverageData(null);
      return;
    }
    const fetchCoverage = async () => {
      setCoverageLoading(true);
      try {
        const res = await inspectionTypesApi.getCoverageSummary({
          inspection_type_id: selectedTypeId,
          cycle: selectedCycle,
          year: selectedYear,
        });
        setCoverageData(res.data);
      } catch (error) {
        console.error('Error fetching coverage summary:', error);
        setCoverageData(null);
      } finally {
        setCoverageLoading(false);
      }
    };
    fetchCoverage();
  }, [selectedTypeId, selectedCycle, selectedYear]);

  // Filter reports based on search
  const filteredReports = reports.filter(report => {
    return report.report_name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Handlers
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleTypeChange = (e) => {
    const typeId = e.target.value;
    setSelectedTypeId(typeId);
    setSelectedCycle('');
    const type = inspectionTypes.find(t => t.id === typeId);
    setSelectedType(type || null);
  };

  // View report in new tab
  const handleViewReport = async (report) => {
    try {
      const response = await api.get(`/reports/${report.id}/download`);
      if (response.data.success && response.data.url) {
        window.open(response.data.url, '_blank');
      } else {
        throw new Error(response.data.error || 'Failed to get download link.');
      }
    } catch (err) {
      console.error('Error viewing report:', err);
    }
  };

  // Download report to disk
  const handleDownloadReport = async (report) => {
    try {
      const response = await api.get(`/reports/${report.id}/download`);
      if (response.data.success && response.data.url) {
        const link = document.createElement('a');
        link.href = response.data.url;
        link.setAttribute('download', response.data.fileName || `${report.report_name}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error(response.data.error || 'Failed to get download link.');
      }
    } catch (err) {
      console.error('Error downloading report:', err);
    }
  };

  // Generate report for a single inspected slope (auto-download)
  const handleGenerateReport = (inspection) => {
    setBatchMode(false);
    setSelectedInspection(inspection);
    setReportModalOpen(true);
  };

  // Generate reports for ALL inspected slopes
  const handleGenerateAll = () => {
    const inspections = coverageData?.inspections || [];
    if (inspections.length === 0) return;
    setBatchMode(true);
    setBatchQueue(inspections);
    setBatchIndex(0);
    setBatchResults([]);
    setSelectedInspection(inspections[0]);
    setReportModalOpen(true);
  };

  // Called after each report is generated (single or batch)
  // Use refs to avoid stale closures in the async callback
  const batchModeRef = useRef(false);
  const batchQueueRef = useRef([]);
  const batchIndexRef = useRef(0);

  useEffect(() => {
    batchModeRef.current = batchMode;
    batchQueueRef.current = batchQueue;
    batchIndexRef.current = batchIndex;
  }, [batchMode, batchQueue, batchIndex]);

  const handleReportGenerated = useCallback((result) => {
    setBatchResults(prev => [...prev, result]);

    if (batchModeRef.current) {
      const nextIndex = batchIndexRef.current + 1;
      if (nextIndex < batchQueueRef.current.length) {
        setBatchIndex(nextIndex);
        setSelectedInspection(batchQueueRef.current[nextIndex]);
      } else {
        // Batch complete
        setBatchMode(false);
        setReportModalOpen(false);
        setSelectedInspection(null);
        fetchReports();
      }
    } else {
      // Single report — close modal
      setReportModalOpen(false);
      setSelectedInspection(null);
      fetchReports();
    }
  }, [fetchReports]);

  const handleReportModalClose = () => {
    setBatchMode(false);
    setBatchQueue([]);
    setBatchIndex(0);
    setReportModalOpen(false);
    setSelectedInspection(null);
    if (batchResults.length > 0) {
      fetchReports();
    }
  };

  // Coverage percentage
  const coveragePercent = coverageData && coverageData.totalAssets > 0
    ? Math.round((coverageData.inspectedCount / coverageData.totalAssets) * 100)
    : 0;

  return (
    <DisciplineAccessControl requiredDiscipline="Geotechnical">
      <Box sx={{ padding: 3, maxWidth: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Slope Inspection Reports
            </Typography>
            <Typography variant="body1" color="text.secondary">
              View and manage slope inspection reports generated from MapView
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              placeholder="Search reports..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                width: { xs: '100%', sm: '240px' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: alpha(theme.palette.background.paper, 0.8),
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
              background: `linear-gradient(45deg, ${alpha(theme.palette.success.main, 0.05)}, ${alpha(theme.palette.success.light, 0.02)})`
            }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: theme.palette.success.main, width: 48, height: 48 }}>
                  <TerrainIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {stats.loading ? <CircularProgress size={20} /> : stats.totalReports}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Total Slope Reports</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
              background: `linear-gradient(45deg, ${alpha(theme.palette.warning.main, 0.05)}, ${alpha(theme.palette.warning.light, 0.02)})`
            }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: theme.palette.warning.main, width: 48, height: 48 }}>
                  <DescriptionIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600} sx={{ fontSize: '0.95rem' }}>
                    {stats.loading ? <CircularProgress size={20} /> : (
                      stats.latestReportDate ? new Date(stats.latestReportDate).toLocaleDateString() : 'N/A'
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Latest Report Date</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Inspection Coverage Tracker */}
        <Paper sx={{ p: 3, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <AssessmentIcon sx={{ color: theme.palette.info.main }} />
            <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              Inspection Coverage Tracker
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Check inspection coverage for slopes by type, cycle, and year
          </Typography>

          {/* Filter Dropdowns - DefectsPage style */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            {/* Inspection Type */}
            <FormControl size="small" sx={dropdownSx(theme)}>
              <Select
                value={selectedTypeId}
                onChange={handleTypeChange}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return <span style={{ color: theme.palette.text.secondary }}>All Inspection Types</span>;
                  }
                  const type = inspectionTypes.find(t => t.id === selected);
                  return type ? type.name : 'All Inspection Types';
                }}
                MenuProps={dropdownMenuProps(theme)}
              >
                {inspectionTypes.map(t => (
                  <MenuItem key={t.id} value={t.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <span>{t.name}</span>
                      {t.is_mandatory && (
                        <Chip
                          label="Mandatory"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.6rem',
                            fontWeight: 600,
                            ml: 'auto',
                            color: '#fff',
                            backgroundColor: '#d32f2f',
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Cycle */}
            <FormControl size="small" sx={dropdownSx(theme)} disabled={!selectedType}>
              <Select
                value={selectedCycle}
                onChange={(e) => setSelectedCycle(e.target.value)}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    if (!selectedType) {
                      return <span style={{ color: theme.palette.text.disabled }}>Select type first</span>;
                    }
                    const freqLabel = selectedType.cycle_frequency
                      ? selectedType.cycle_frequency.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                      : 'Cycle';
                    return <span style={{ color: theme.palette.text.secondary }}>Select {freqLabel} Cycle</span>;
                  }
                  const opt = getCycleOptions(selectedType?.cycle_frequency).find(o => o.value === selected);
                  return opt ? opt.label : `Cycle ${selected}`;
                }}
                MenuProps={dropdownMenuProps(theme)}
              >
                {getCycleOptions(selectedType?.cycle_frequency).map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Year */}
            <FormControl size="small" sx={dropdownSx(theme)}>
              <Select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return <span style={{ color: theme.palette.text.secondary }}>Select Year</span>;
                  }
                  return `Year ${selected}`;
                }}
                MenuProps={dropdownMenuProps(theme)}
              >
                {yearOptions.map(y => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Loading */}
          {coverageLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          )}

          {/* Prompt to select */}
          {!coverageData && !coverageLoading && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <DonutSmallIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">
                Select an inspection type, cycle, and year to view coverage
              </Typography>
            </Box>
          )}

          {/* Results */}
          {coverageData && !coverageLoading && (
            <>
              {/* Coverage Stat Cards - CSS Grid (AssetsPage pattern) */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, 1fr)',
                    md: 'repeat(4, 1fr)',
                  },
                  gap: 2,
                  mb: 3
                }}
              >
                {/* Total Slopes */}
                <Card
                  sx={{
                    boxShadow: theme.shadows[2],
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #5b86e5, #36d1dc)',
                    color: 'white',
                    minHeight: '120px'
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <TerrainIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
                      <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {coverageData.totalAssets}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        Total Slopes
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>

                {/* Inspected */}
                <Card
                  sx={{
                    boxShadow: theme.shadows[2],
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #4caf50, #81c784)',
                    color: 'white',
                    minHeight: '120px'
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <CheckCircleIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
                      <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {coverageData.inspectedCount}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        Inspected
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>

                {/* Uninspected */}
                <Card
                  sx={{
                    boxShadow: theme.shadows[2],
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #ff7043, #ffab40)',
                    color: 'white',
                    minHeight: '120px'
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <CancelIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
                      <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {coverageData.uninspectedCount}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        Uninspected
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>

                {/* Coverage % */}
                <Card
                  sx={{
                    boxShadow: theme.shadows[2],
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #9c27b0, #ba68c8)',
                    color: 'white',
                    minHeight: '120px'
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <PercentIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
                      <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {coveragePercent}%
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        Coverage
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              {/* Status Breakdown */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Inspection Status Breakdown
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {Object.entries(coverageData.statusBreakdown).map(([status, count]) => {
                    const config = statusConfig[status] || { label: status, color: '#9e9e9e' };
                    return (
                      <Chip
                        key={status}
                        size="small"
                        label={`${config.label}: ${count}`}
                        sx={{
                          color: '#fff',
                          backgroundColor: config.color,
                          fontWeight: 600,
                          fontSize: '0.65rem',
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>

              {/* Duplicate Warnings */}
              {coverageData.duplicates && coverageData.duplicates.length > 0 && (
                <Alert
                  severity="warning"
                  icon={<WarningAmberIcon />}
                  sx={{ mt: 2, borderRadius: '8px' }}
                >
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                    {coverageData.duplicates.length} asset{coverageData.duplicates.length > 1 ? 's' : ''} with duplicate inspections
                  </Typography>
                  {coverageData.duplicates.map(dup => (
                    <Typography key={dup.asset_id} variant="body2">
                      {dup.asset_name || 'Unknown'} ({dup.asset_code || dup.asset_id}) &mdash; {dup.count} inspections
                    </Typography>
                  ))}
                </Alert>
              )}

              {/* Inspected Slopes Table */}
              {coverageData.inspections && coverageData.inspections.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Inspected Slopes ({coverageData.inspections.length})
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={handleGenerateAll}
                      sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        px: 2,
                        py: 0.75,
                      }}
                    >
                      Generate All Reports ({coverageData.inspections.length})
                    </Button>
                  </Box>
                  <TableContainer sx={{
                    borderRadius: '10px',
                    boxShadow: 'none',
                    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`
                  }}>
                    <Table size="small" sx={{ minWidth: 650 }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: alpha(theme.palette.info.main, 0.04) }}>
                          <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Asset Code</TableCell>
                          <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Asset Name</TableCell>
                          <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Inspector</TableCell>
                          <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Inspection Date</TableCell>
                          <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Condition</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, py: 1.5 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {coverageData.inspections.map((insp) => {
                          const config = statusConfig[insp.status] || { label: insp.status, color: '#9e9e9e' };
                          return (
                            <TableRow
                              key={insp.id}
                              hover
                              sx={{
                                '&:hover': { backgroundColor: alpha(theme.palette.info.main, 0.04) },
                                '& .MuiTableCell-root': { py: 1, borderColor: alpha(theme.palette.divider, 0.08) }
                              }}
                            >
                              <TableCell>
                                <Chip
                                  label={insp.asset_code || 'N/A'}
                                  size="small"
                                  sx={{
                                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                                    color: theme.palette.info.main,
                                    fontSize: '0.75rem',
                                    borderRadius: '6px',
                                    fontWeight: 600
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>{insp.asset_name}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{insp.inspector_name || 'N/A'}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {insp.inspection_date ? new Date(insp.inspection_date).toLocaleDateString() : 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={config.label}
                                  size="small"
                                  sx={{
                                    color: '#fff',
                                    backgroundColor: config.color,
                                    fontWeight: 600,
                                    fontSize: '0.65rem'
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {insp.condition_rating_name || 'Not Rated'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Generate PDF Report">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleGenerateReport(insp)}
                                    sx={{
                                      color: theme.palette.primary.main,
                                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                      '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.15) }
                                    }}
                                  >
                                    <PictureAsPdfIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </>
          )}
        </Paper>

        {/* Reports Table */}
        <Paper sx={{ p: 3, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              Slope Inspection Reports
            </Typography>
          </Box>

          <TableContainer sx={{ borderRadius: '10px', boxShadow: 'none', border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha(theme.palette.success.main, 0.04) }}>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Report Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Asset ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Asset Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Generated</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Format</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Generated By</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingReports ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 4, textAlign: 'center' }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredReports.map((report) => {
                  return (
                    <TableRow
                      key={report.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.success.main, 0.04),
                        },
                        '& .MuiTableCell-root': {
                          py: 1.5,
                          borderColor: alpha(theme.palette.divider, 0.08)
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PictureAsPdfIcon sx={{ mr: 1.5, color: theme.palette.error.main }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {report.report_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={report.asset_id || 'N/A'}
                          size="small"
                          sx={{
                            backgroundColor: alpha(theme.palette.success.main, 0.1),
                            color: theme.palette.success.main,
                            fontSize: '0.75rem',
                            borderRadius: '6px',
                            fontWeight: 600
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {report.asset_name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {new Date(report.created_at).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(report.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label="PDF"
                          size="small"
                          sx={{
                            backgroundColor: alpha(theme.palette.grey[500], 0.1),
                            color: theme.palette.text.primary,
                            fontSize: '0.75rem',
                            borderRadius: '6px',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{report.generated_by_name || 'Unknown'}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                          <Tooltip title="View Report">
                            <IconButton
                              size="small"
                              onClick={() => handleViewReport(report)}
                              sx={{
                                color: theme.palette.success.main,
                                backgroundColor: alpha(theme.palette.success.main, 0.08),
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.success.main, 0.15),
                                }
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download PDF">
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadReport(report)}
                              sx={{
                                color: theme.palette.primary.main,
                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.15),
                                }
                              }}
                            >
                              <FileDownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Share">
                            <IconButton
                              size="small"
                              sx={{
                                color: theme.palette.info.main,
                                backgroundColor: alpha(theme.palette.info.main, 0.08),
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.info.main, 0.15),
                                }
                              }}
                            >
                              <ShareIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {!loadingReports && filteredReports.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 4, textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <TerrainIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                        <Typography color="text.secondary">
                          No slope inspection reports found. Generate reports from the MapView inspection modal.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Slope Report Preview Modal */}
        <SlopeReportPreviewModal
          open={reportModalOpen}
          onClose={handleReportModalClose}
          inspection={selectedInspection}
          autoDownload
          onReportGenerated={handleReportGenerated}
          batchProgress={batchMode ? { current: batchIndex + 1, total: batchQueue.length } : null}
        />
      </Box>
    </DisciplineAccessControl>
  );
};

export default SlopeReportsPage;
